// =============================================================================
// ACTION HANDLER UTILITY
// =============================================================================
// Centralized handling for all user actions (buttons, links, etc.)
// Eliminates code duplication across 12+ components

/**
 * Action configuration interface
 * Replaces the old ButtonConfig with more flexible action types
 */
export interface ActionConfig {
  id?: string;
  type: 'navigate' | 'scroll' | 'external' | 'modal' | 'download' | 'payment' | 'custom';
  label: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  
  // Navigation actions
  url?: string;          // For navigate/external/download
  
  // Scroll actions
  target?: string;       // Element ID to scroll to
  offset?: number;       // Scroll offset in pixels
  
  // Modal actions
  modalId?: string;      // Modal to open
  modalData?: unknown;   // Data to pass to modal
  
  // Payment actions
  paymentConfig?: {
    type: 'one-time' | 'recurring-weekly' | 'recurring-monthly' | 'recurring-yearly';
    category: 'professional-services' | 'subscription-content' | 'course-package' | 'consultation';
    amount: number;
    currency: string;
    name: string;
    description: string;
    interval?: 'week' | 'month' | 'year';
    intervalCount?: number;
    stripePriceId?: string;
    metadata?: Record<string, string>;
    successUrl?: string;
    cancelUrl?: string;
  };
  
  // Custom actions
  handler?: string;      // Name of custom handler function
  data?: unknown;        // Data to pass to handler
  
  // UI properties
  icon?: string;         // Icon name or component
  disabled?: boolean;    // Is action disabled
  loading?: boolean;     // Is action in loading state
  
  // Metadata
  analytics?: {
    event?: string;
    category?: string;
    label?: string;
  };
}

/**
 * Result of an action execution
 */
export interface ActionResult {
  success: boolean;
  error?: string;
  data?: unknown;
}

/**
 * Custom action handler function type
 */
export type CustomActionHandler = (action: ActionConfig) => Promise<ActionResult> | ActionResult;

/**
 * Registry for custom action handlers
 */
const customHandlers = new Map<string, CustomActionHandler>();

/**
 * Register a custom action handler
 */
export function registerActionHandler(name: string, handler: CustomActionHandler): void {
  customHandlers.set(name, handler);
}

/**
 * Unregister a custom action handler
 */
export function unregisterActionHandler(name: string): void {
  customHandlers.delete(name);
}

/**
 * Main action handler - handles all action types
 */
export async function handleAction(action: ActionConfig): Promise<ActionResult> {
  try {
    // Check if action is disabled
    if (action.disabled) {
      return {
        success: false,
        error: 'Action is disabled',
      };
    }

    // Track analytics if configured
    if (action.analytics) {
      trackAction(action);
    }

    // Handle different action types
    switch (action.type) {
      case 'navigate':
        return handleNavigate(action);
      
      case 'external':
        return handleExternal(action);
      
      case 'scroll':
        return handleScroll(action);
      
      case 'modal':
        return handleModal(action);
      
      case 'download':
        return handleDownload(action);
      
      case 'payment':
        return handlePayment(action);
      
      case 'custom':
        return await handleCustom(action);
      
      default:
        return {
          success: false,
          error: `Unknown action type: ${action.type}`,
        };
    }
  } catch (error) {
    console.error('Error handling action:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Handle internal navigation
 */
function handleNavigate(action: ActionConfig): ActionResult {
  if (!action.url) {
    return { success: false, error: 'No URL provided for navigation' };
  }

  try {
    window.location.href = action.url;
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Navigation failed',
    };
  }
}

/**
 * Handle external link
 */
function handleExternal(action: ActionConfig): ActionResult {
  if (!action.url) {
    return { success: false, error: 'No URL provided for external link' };
  }

  try {
    window.open(action.url, '_blank', 'noopener,noreferrer');
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to open external link',
    };
  }
}

/**
 * Handle smooth scroll to element
 */
function handleScroll(action: ActionConfig): ActionResult {
  if (!action.target) {
    return { success: false, error: 'No target provided for scroll' };
  }

  try {
    const element = document.getElementById(action.target);
    if (!element) {
      return { success: false, error: `Element not found: ${action.target}` };
    }

    const offset = action.offset || 0;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Scroll failed',
    };
  }
}

/**
 * Handle modal opening
 */
function handleModal(action: ActionConfig): ActionResult {
  if (!action.modalId) {
    return { success: false, error: 'No modal ID provided' };
  }

  try {
    // Dispatch custom event for modal management
    const event = new CustomEvent('openModal', {
      detail: {
        modalId: action.modalId,
        data: action.modalData,
      },
    });
    window.dispatchEvent(event);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to open modal',
    };
  }
}

/**
 * Handle file download
 */
function handleDownload(action: ActionConfig): ActionResult {
  if (!action.url) {
    return { success: false, error: 'No URL provided for download' };
  }

  try {
    const link = document.createElement('a');
    link.href = action.url;
    link.download = action.label || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Download failed',
    };
  }
}

/**
 * Handle payment action
 */
function handlePayment(action: ActionConfig): ActionResult {
  if (!action.paymentConfig) {
    return { success: false, error: 'No payment configuration provided' };
  }

  try {
    // Encode payment config as URL parameter
    const configParam = encodeURIComponent(JSON.stringify(action.paymentConfig));
    const paymentUrl = `/payment?config=${configParam}`;
    
    // Navigate to payment page
    window.location.href = paymentUrl;
    
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Payment redirect failed',
    };
  }
}

/**
 * Handle custom action
 */
async function handleCustom(action: ActionConfig): Promise<ActionResult> {
  if (!action.handler) {
    return { success: false, error: 'No handler specified for custom action' };
  }

  const handler = customHandlers.get(action.handler);
  if (!handler) {
    return {
      success: false,
      error: `Custom handler not found: ${action.handler}`,
    };
  }

  try {
    const result = await handler(action);
    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Custom handler failed',
    };
  }
}

/**
 * Track action for analytics
 */
function trackAction(action: ActionConfig): void {
  if (!action.analytics) return;

  // Check if Google Analytics is available
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', action.analytics.event || 'action', {
      event_category: action.analytics.category || 'engagement',
      event_label: action.analytics.label || action.label,
      action_type: action.type,
    });
  }
}
/**
 * Create an action handler for a Button component
 * Returns a function that can be passed directly to onClick
 */
export function createActionHandler(action: ActionConfig) {
  return async () => {
    const result = await handleAction(action);
    if (!result.success && result.error) {
      console.error('Action failed:', result.error);
    }
  };
}

/**
 * Batch execute multiple actions
 */
export async function handleActions(actions: ActionConfig[]): Promise<ActionResult[]> {
  return Promise.all(actions.map(handleAction));
}

/**
 * Convert legacy ButtonConfig to ActionConfig
 * For backward compatibility during migration
 */
export function convertButtonToAction(button: {
  label: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  action?: 'scroll' | 'external' | 'internal' | 'payment';
  target?: string;
  url?: string;
  paymentType?: 'one-time' | 'recurring-weekly' | 'recurring-monthly' | 'recurring-yearly';
  paymentConfig?: {
    type: 'one-time' | 'recurring-weekly' | 'recurring-monthly' | 'recurring-yearly';
    category: 'professional-services' | 'subscription-content' | 'course-package' | 'consultation';
    amount: number;
    currency: string;
    name: string;
    description: string;
    interval?: 'week' | 'month' | 'year';
    intervalCount?: number;
    stripePriceId?: string;
    metadata?: Record<string, string>;
    successUrl?: string;
    cancelUrl?: string;
  };
}): ActionConfig {
  let type: ActionConfig['type'] = 'navigate';
  
  if (button.action === 'scroll') {
    type = 'scroll';
  } else if (button.action === 'external') {
    type = 'external';
  } else if (button.action === 'internal') {
    type = 'navigate';
  } else if (button.action === 'payment') {
    type = 'payment';
  }

  const actionConfig: ActionConfig = {
    type,
    label: button.label,
    variant: button.variant,
    size: button.size,
    url: button.url,
    target: button.target,
  };

  // Add payment config if this is a payment action
  if (type === 'payment' && button.paymentConfig) {
    actionConfig.paymentConfig = button.paymentConfig;
  }

  return actionConfig;
}

